import { ListService } from "@/modules/List/services/ListService";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { FORM_MESSAGES } from "@/shared/constants/FormConstants";
import { Http } from "@status/codes";
import { AxiosError } from "axios";
import { Vue } from "vue-property-decorator";
import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import { GENERAL_RULES_CONSTANTS } from "@/modules/GeneralRules/constants/GeneralRulesConstants";
import { UserService } from "@/modules/User/services/UserService";
import { USER_ACCESS } from "@/modules/User/constants/UserConstants";
import { UserQuery } from "@/modules/User/models/UserQuery";
import { CupsPriceLevelService } from "@/modules/CupsPriceLevels/services/CupsPriceLevelService";
import { CUPS_ACCESS } from "@/modules/CupsPriceLevels/constants/CupsPriceLevelConstants";
import {
  FilterField,
  FilterOfCollection
} from "@/modules/DashboardTemplate/models/FilterOfCollection";
import { SectionOfDashboard } from "@/modules/DashboardTemplate/models/SectionOfDashboard";
import { FIELD_WITHOUT_DATA } from "@/modules/DashboardTemplate/constants/DashboardTemplatesConstants";
import store from "@/store";
import { ServiceTemplateService } from "@/modules/ServiceTemplate/services/ServiceTemplateService";
import { FormTemplateService } from "@/modules/FormTemplate/services/FormTemplateService";
import { QuerySend, QuerySendMultiple } from "@/shared/models/FormQuerySend";
import { DependenciesService } from "@/modules/Dependencies/services/DependenciesService";
import { LACK_OF_OPTIONS } from "@/modules/Dashboards/AdminDashboard/constants/AdminDashboardConstants";
import { DEPENDENCIES_ACCESS } from "@/modules/Dependencies/constants/DependenciesConstants";
import { CampusService } from "@/modules/Campus/services/CampusService";
import { SpaceService } from "@/modules/Space/services/SpaceService";
import { AvailabilityTemplateService } from "@/modules/AvailabilityTemplate/services/AvailabilityTemplateService";

export abstract class ListOfPropertiesValues extends Vue {
  abstract loadItems(
    field: string,
    idList?: string,
    keyword?: string,
    lastDocId?: string
  ): Promise<{ data: DynamicSelectData[]; lastDocId?: string }>;

  abstract setIndexes(
    indexSectionOfDashboard: number,
    indexCollectionFilter: number
  ): void;

  async getList(idList: string): Promise<{ data: DynamicSelectData[] }> {
    try {
      const listService = new ListService();
      const listResponse = await listService.getListById(idList);
      if (listResponse.code === Http.Found) {
        const dataList = (listResponse.data?.options.map(item => {
          return {
            id: item.id,
            name: item.title
          };
        }) as unknown) as DynamicSelectData[];
        dataList.push(FIELD_WITHOUT_DATA);
        return {
          data: dataList
        };
      }
      return { data: [] };
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
      return { data: [] };
    }
  }

  async getUserByQuery(
    setFilters: (ref: string, name: string) => { UserQuery: UserQuery },
    name?: string
  ) {
    try {
      if (!name) return { data: [] };

      const filterQuery = setFilters(
        GENERAL_RULES_CONSTANTS.ID_REFERENCE,
        name
      );

      const userService = new UserService();
      const response = await userService.getUserByFilters(
        filterQuery as UserQuery
      );

      if (response.code !== Http.Found || !response.data) {
        Snackbar.popInfo(response.message);
        return { data: [] };
      }
      const users = response.data.userArray;
      const userList: DynamicSelectData[] = [];
      users.forEach(item => {
        const user =
          item.name && item.name != "" ? item.name : item.userDocument ?? "";
        userList.push({
          id: item.id,
          name: user
        });
      });

      return { data: userList };
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        return Snackbar.popError(
          FORM_MESSAGES.FORBIDDEN_REQUEST + USER_ACCESS.GET
        );
      else return Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getCups() {
    try {
      const cupsPriceLevelService = new CupsPriceLevelService();
      const responseData = await cupsPriceLevelService.getAllActiveCupsPriceLevel();
      if (responseData.code === Http.Found && responseData.data !== undefined) {
        const serviceCups = responseData.data;
        const serviceCupsList: DynamicSelectData[] = [];
        serviceCups.forEach(cup => {
          serviceCupsList.push({
            id: cup.id,
            name: `${cup.cups} - ${cup.externalName}`,
            fields: cup.clients
          });
        });
        return { data: serviceCupsList };
      }
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(FORM_MESSAGES.FORBIDDEN_REQUEST + CUPS_ACCESS.GET);
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getPriceLevels(
    sectionsOfDashboardStore: SectionOfDashboard[],
    indexSectionOfDashboard: number,
    indexCollection: number
  ) {
    const fieldFound: FilterOfCollection = sectionsOfDashboardStore[
      indexSectionOfDashboard
    ].collections[indexCollection].filters.find(
      (item: FilterOfCollection) =>
        (item.field as FilterField[])[0].id == "cups"
    ) as FilterOfCollection;
    const values: DynamicSelectData[] = [];

    fieldFound.value.forEach((item: DynamicSelectData) => {
      Object.entries(item.fields).forEach(([key, value]) => {
        const item = value as { name: string; price: string };
        values.push({
          id: key,
          name: item.name + " - " + item.price
        });
      });
    });

    return { data: values };
  }

  async addSectionsOrGroupsCommonFunction(
    valueOfField: { id: string; name: string }[],
    indexSectionOfDashboard: number
  ) {
    if (valueOfField.length > 1) {
      const functionsToExecute = [
        {
          data: {
            id: "groupKey",
            name: "Grupos",
            fields: [] as { id: string; name: string }[],
            type: "list"
          },
          function: "removeField"
        },
        {
          data: {
            id: "sectionKey",
            name: "Grupos",
            fields: [] as { id: string; name: string }[],
            type: "list"
          },
          function: "removeField"
        }
      ];

      return functionsToExecute;
    }

    const serviceTemplateService = new ServiceTemplateService();
    const response = await serviceTemplateService.getServiceTemplateById(
      valueOfField[0].id
    );
    if (response.code === Http.Found && response.data) {
      if (response.data.type == "sections") {
        store.commit("setDataToUseInValuesSelect", {
          id: "sections",
          data: response.data.sections,
          indexSectionOfDashboard: indexSectionOfDashboard
        });

        const functionsToExecute = [
          {
            data: {
              id: "groupKey",
              name: "Grupos",
              fields: [] as { id: string; name: string }[],
              type: "list"
            },
            function: "removeField"
          }
        ];

        if (response.data.sections!.length > 0) {
          functionsToExecute.push({
            data: {
              id: "sectionKey",
              name: "Secciones",
              fields: (response.data.sections! as unknown) as {
                id: string;
                name: string;
              }[],
              type: "list"
            },
            function: "addNewField"
          });
          return functionsToExecute;
        }

        return functionsToExecute;
      }
      store.commit("setDataToUseInValuesSelect", {
        id: "groups",
        data: response.data.groups,
        indexSectionOfDashboard: indexSectionOfDashboard
      });

      const functionsToExecute = [
        {
          data: {
            id: "sectionKey",
            name: "Secciones",
            fields: [] as { id: string; name: string }[],
            type: "list"
          },
          function: "removeField"
        }
      ];

      if (response.data.groups!.length > 0) {
        functionsToExecute.push({
          data: {
            id: "groupKey",
            name: "Grupos",
            fields: (response.data.groups! as unknown) as {
              id: string;
              name: string;
            }[],
            type: "list"
          },
          function: "addNewField"
        });
        return functionsToExecute;
      }

      return functionsToExecute;
    }
  }

  getSectionsCommonFunctions(indexSectionOfDashboard: number) {
    const data = store.getters.getDataToUseInValuesSelect[
      indexSectionOfDashboard
    ].sections.map((section: any) => ({ id: section.key, name: section.name }));
    return { data };
  }
  getGroupsCommonFunctions(indexSectionOfDashboard: number) {
    const data = store.getters.getDataToUseInValuesSelect[
      indexSectionOfDashboard
    ].groups.map((group: any) => ({ id: group.key, name: group.name }));
    return { data };
  }

  async getServiceTemplatesCommonFunction() {
    try {
      const serviceTemplateService = new ServiceTemplateService();
      const response = await serviceTemplateService.getAllActiveServiceTemplate();
      if (response.code === Http.Found && response.data) {
        const serviceTemplatesList: DynamicSelectData[] = [];
        response.data.forEach(({ id, internalName }) => {
          serviceTemplatesList.push({
            id: id as string,
            name: internalName
          });
        });
        serviceTemplatesList.push(FIELD_WITHOUT_DATA);
        return { data: serviceTemplatesList };
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getFormTemplatesCommonFunction(lastDocId: string, keyword?: string) {
    try {
      const formTemplateService = new FormTemplateService();
      const query: QuerySend = {
        state: GENERAL_RULES_CONSTANTS.ACTIVO,
        docref: lastDocId
      };
      if (keyword) {
        query.name = keyword;
      }

      const response = await formTemplateService.getFormTemplateWithFilters(
        query
      );
      if (response.code === Http.Found && response.data) {
        const formTemplatesList: DynamicSelectData[] = [];
        response.data.data.forEach((item: any) => {
          const _key = item?._key ?? "";
          const name = item?.name ?? "";
          formTemplatesList.push({
            id: String(_key),
            name: name
          });
        });
        formTemplatesList.push(FIELD_WITHOUT_DATA);
        return { data: formTemplatesList, lastDocId: response.data.docref };
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getDependenciesListCommonFunction(lastDocId: string, keyword?: string) {
    try {
      const filter: QuerySendMultiple = {
        state: [GENERAL_RULES_CONSTANTS.ACTIVO]
      };

      if (lastDocId) {
        filter.docref = lastDocId;
        filter.paginatedResult = true;
      }

      if (keyword) {
        filter.name = keyword;
      }

      const dependenciesService = new DependenciesService();
      const responseData = await dependenciesService.getDependenciesWithFilters(
        filter
      );
      if (responseData.code === Http.Found && responseData.data) {
        const depenednciesList: DynamicSelectData[] = [];
        const dataList = responseData.data.data;
        dataList.forEach(dependency =>
          depenednciesList.push({ id: dependency.id!, name: dependency.name })
        );
        depenednciesList.push(FIELD_WITHOUT_DATA);
        return { data: depenednciesList, lastDocId: responseData.data.docRef };
      } else {
        Snackbar.popInfo(LACK_OF_OPTIONS);
      }
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        Snackbar.popError(
          FORM_MESSAGES.FORBIDDEN_REQUEST + DEPENDENCIES_ACCESS.GET
        );
      else Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getAvailabilityTemplatesCommonFunction(
    lastDocId: string,
    keyword: string
  ) {
    try {
      const availabilityTemplateService = new AvailabilityTemplateService();
      const response = await availabilityTemplateService.getAvailabitiesTemplate(
        lastDocId,
        keyword
      );
      if (response.code === Http.Found && response.data) {
        const availabilityTemplatesList: DynamicSelectData[] = [];
        response.data.availabilitiesTemplates.forEach(({ id, name }) => {
          availabilityTemplatesList.push({
            id: id as string,
            name
          });
        });
        availabilityTemplatesList.push(FIELD_WITHOUT_DATA);
        return {
          data: availabilityTemplatesList,
          lastDocId: response.data.lastReference
        };
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getCampusCommonFunction() {
    try {
      const campusService = new CampusService();
      const response = await campusService.getAllCampus();
      if (response.code === Http.Found && response.data) {
        const campusList: DynamicSelectData[] = [];
        response.data.forEach(({ key, name }: any) => {
          campusList.push({
            id: key as string,
            name: name as string
          });
        });
        campusList.push(FIELD_WITHOUT_DATA);
        return { data: campusList };
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getSpacesCommonFunction() {
    try {
      const spaceService = new SpaceService();
      const response = await spaceService.getAllSpaces();
      if (response.code === Http.Found && response.data) {
        const spacesList: DynamicSelectData[] = [];
        response.data.forEach(({ key, name }: any) => {
          spacesList.push({
            id: key as string,
            name: name as string
          });
        });
        spacesList.push(FIELD_WITHOUT_DATA);
        return { data: spacesList };
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getDocument(document?: string) {
    try {
      const userService = new UserService();
      const response = await userService.getUserByFilters({
        userDocument: document
      });
      if (response.code === Http.Found) {
        return {
          data: response.data?.userArray.map(user => ({
            id: user.id,
            name: user.userDocument
          }))
        };
      }
      Snackbar.popInfo(response.message);
      return { data: [] };
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        return Snackbar.popError(
          FORM_MESSAGES.FORBIDDEN_REQUEST + USER_ACCESS.GET
        );
    }
  }
}
